/**
 * Campaign attribution across the website → app hop. The website forwards utm_* on its links into
 * the app; the app keeps the latest set (and the page it landed on) for the visit and sends it with
 * the registration, so a new account can be traced to the campaign that brought it. Browser storage
 * may be unavailable (private mode); attribution is then simply lost, never an error.
 */
const KEY = "av.attribution";
const FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export type Attribution = Partial<Record<(typeof FIELDS)[number] | "landing_path", string>>;

export function attributionFrom(search: string, pathname: string): Attribution | null {
  const params = new URLSearchParams(search);
  const found: Attribution = {};
  for (const field of FIELDS) {
    const value = params.get(field)?.trim();
    if (value) found[field] = value.slice(0, 120);
  }
  if (!Object.keys(found).length) return null;
  found.landing_path = pathname.slice(0, 120);
  return found;
}

/** Runs once at start-up: a visit that arrives with campaign parameters replaces the stored set. */
export function captureAttribution(location: { search: string; pathname: string } = window.location): void {
  const found = attributionFrom(location.search, location.pathname);
  if (!found) return;
  try { window.localStorage.setItem(KEY, JSON.stringify(found)); } catch { /* storage unavailable */ }
}

export function storedAttribution(): Attribution | undefined {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Attribution) : undefined;
  } catch {
    return undefined;
  }
}

export function clearAttribution(): void {
  try { window.localStorage.removeItem(KEY); } catch { /* storage unavailable */ }
}
